import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppContext } from '../../../app/provider/useAppContext';
import { appConfig } from '../../../config/app-config';
import { resolveCartOnLogin } from '../../../features/cart/api/cartApi';
import { emitCartUpdate } from '../../../features/cart/utils/cartUtil';
import { AUTH_STORAGE_KEYS, AUTH_CONFIG } from '../constants/auth.constants';
import * as loginService from '../services/login';
import * as logoutService from '../services/logout';
import * as sessionService from '../services/session';
import { UserProfile } from '../types/auth.types';

export const useAuth = () => {
    const [context, setContext] = useAppContext();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [showLogoutPopup, setShowLogoutPopup] = useState(false);

    // Initial Auth Check (Matches PingLogin logic from OneInformaBar)
    useEffect(() => {
        const checkAuthStatus = async () => {
            setIsLoading(true);
            const userInfo = await sessionService.getUserInfo();
            setIsLoading(false);

            if (userInfo) {
                setContext(prev => {
                    if (prev.loggedIn) return prev;
                    return {
                        ...prev,
                        loggedIn: true,
                        userInfo: userInfo as UserProfile,
                        authType: AUTH_CONFIG.PROVIDER_PING,
                        // Use stored accessToken if available
                        accessToken: localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN)
                    };
                });
            }
        };
        checkAuthStatus();
    }, [setContext]);

    const handleLogin = async () => {
        setIsLoading(true);
        // Store current location for redirect after login
        const currentPath = window.location.pathname + window.location.search;
        localStorage.setItem(AUTH_STORAGE_KEYS.POST_LOGIN_REDIRECT, currentPath);

        const url = await loginService.getLoginUrl();
        window.location.href = url;
    };

    const handleLogout = async () => {
        setIsLoading(true);
        // Get current cart ID from context to ensure logout confirmation detection is reliable
        const cartId = context.cart?.id || null;
        const result = await logoutService.logout(cartId);
        setIsLoading(false);

        if (result.needsConfirmation && result.hasActiveCart) {
            setShowLogoutPopup(true);
        } else {
            finalizeLogout();
        }
    };

    const finalizeLogout = () => {
        setContext({
            ...context,
            loggedIn: false,
            accessToken: null,
            userInfo: null,
            authType: null,
            cart: null
        });
        setShowLogoutPopup(false);
        emitCartUpdate(null);

        // Use setTimeout to ensure state updates complete before redirect
        setTimeout(() => {
            const pathParts = window.location.pathname.split('/').filter(Boolean);
            // URL structure is /:storeKey/:eventKey
            const storeKey = pathParts.length > 0 ? pathParts[0] : appConfig.defaults.storeKey;
            const eventKey = pathParts.length > 1 ? pathParts[1] : appConfig.defaults.eventKey;
            navigate(`/${storeKey}/${eventKey}`, { replace: true });
        }, 100);
    };

    const performLogoutConfirm = async () => {
        setIsLoading(true);
        const cartId = context.cart?.id || null;
        await logoutService.confirmLogout(cartId);
        setIsLoading(false);
        finalizeLogout();
    };

    const cancelLogout = () => {
        setShowLogoutPopup(false);
    };

    return {
        loggedIn: context.loggedIn,
        userInfo: context.userInfo as UserProfile | null,
        isLoading,
        showLogoutPopup,
        handleLogin,
        handleLogout,
        performLogoutConfirm,
        cancelLogout,
        finalizeLogout
    };
};
